import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Input,
  Typography,
  useMediaQuery,
} from '@mui/material';
import dayjs from 'dayjs';
import React, {
  ChangeEvent,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ButtonTypeEnum } from '../../enums/button-type-enum';
import { EducationLevelEnum } from '../../enums/education-type-enum';
import { LabelTypeEnum } from '../../enums/label-enum';
import { SnackbarSeverityEnum } from '../../enums/snackbar-severity-enum';
import { useModal } from '../../hooks/useModal';
import {
  IEducationData,
  IEducationInputs,
} from '../../interfaces/education.interface';
import { ProfilelInfoFormInputs } from '../../interfaces/profile.interface';
import { IUser } from '../../interfaces/user.interface';
import SectionLayout from '../../layouts/SectionLayout';
import gamificationApi from '../../store/apiSlice/gamification/gamificationApi';
import { useUpdateMyProfileMutation } from '../../store/apiSlice/userApi';
import {
  useCreateUpdateEducationMutation,
  useDeleteEducationMutation,
} from '../../store/apiSlice/userEducationApi';
import { useThemeContext } from '../../theme/ThemeContextProvider';
import { errorHelper } from '../../utils/helper/error-helper';
import { profileSchema } from '../../validation/user-validation';
import CameraIcon from '../Icons/CameraIcon';
import CloseX from '../Icons/CloseX';
import ProfilePhotoEditorModal from '../Modals/ProfilePhotoEditorModal';
import CustomButton from '../MUIComponents/CustomButton';
import Label from '../MUIComponents/Label';
import SnackbarCustom from '../MUIComponents/SnackbarCustom';
import ProfileEducationForm from './Forms/ProfileEducationForm/ProfileEducationForm';
import ProfilePersonalInfoForm from './Forms/ProfilePersonalInfoForm/ProfilePersonalInfoForm';

interface PersonalInfoProps {
  user?: IUser;
}

const PersonalInfo = ({ user }: PersonalInfoProps) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const educationSectionRef = useRef<HTMLDivElement | null>(null);

  const { state } = location as { state: { moveToEducationBlock?: boolean } };

  const [originImageUrl, setOriginImageUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { isOpen, openModal, closeModal } = useModal();

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<SnackbarSeverityEnum>(SnackbarSeverityEnum.ERROR);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  // Use unified user data provided by the parent (ProfilePage) so we don't
  // trigger an extra /me?include=educations request from this component.
  const profileInfo = user;
  const educationInfo = user?.educations;
  const isProfileInfoLoading = !profileInfo;
  const isEducationInfoLoading = !educationInfo;

  const [updateMyProfile] = useUpdateMyProfileMutation();
  const [createUpdateEducation] = useCreateUpdateEducationMutation();
  const [deleteEducation] = useDeleteEducationMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProfilelInfoFormInputs>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      birthdate: null,
      isBirthdateVisible: false,
      bio: '',
      country: '',
      city: '',
      educations: [],
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'educations',
  });

  useEffect(() => {
    if (
      !isProfileInfoLoading &&
      !isEducationInfoLoading &&
      profileInfo &&
      educationInfo
    ) {
      const formData: ProfilelInfoFormInputs = {
        firstName: profileInfo?.firstName || null,
        lastName: profileInfo?.lastName || null,
        birthdate: profileInfo?.birthdate
          ? dayjs(profileInfo?.birthdate)
          : null,
        isBirthdateVisible: profileInfo?.isBirthdateVisible || false,
        bio: profileInfo?.bio || null,
        country: profileInfo?.country || null,
        city: profileInfo?.city || null,
        educations: educationInfo?.length
          ? educationInfo.map((education: any) => ({
              id: education.id,
              educationId: education.id,
              universityName: education.university,
              idUniversity: education.idUniversity || null,
              specialtyName: education.specialty,
              idSpecialty: education.idSpecialty || null,
              level: education.level || EducationLevelEnum.ELEMENTARY,
              startYear: dayjs(education.startYear),
              endYear: dayjs(education.endYear),
            }))
          : [],
      };

      // Reset the form to mirror the latest backend snapshot and clear any
      // dirty state so isDirty reflects only user edits after this point.
      reset(formData, { keepDirty: false });
    }
  }, [
    isProfileInfoLoading,
    isEducationInfoLoading,
    profileInfo,
    educationInfo,
    reset,
  ]);

  useEffect(() => {
    return () => {
      if (originImageUrl && originImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(originImageUrl);
      }
    };
  }, [originImageUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const addEducation = () => {
    append({});
  };

  const removeEducation = (index: number) => {
    const educationId = (fields[index] as IEducationInputs).educationId;
    remove(index);
    if (educationId) deleteEducation(educationId);
  };

  const onSubmit: SubmitHandler<ProfilelInfoFormInputs> = async (data) => {
    const { educations, ...profileData } = data;
    const personalInfoFormData = new FormData();

    if (selectedImage) {
      personalInfoFormData.append('file', selectedImage);
    }

    if (profileData.firstName) {
      personalInfoFormData.append('firstName', profileData.firstName);
    }

    if (profileData.lastName) {
      personalInfoFormData.append('lastName', profileData.lastName);
    }

    if (profileData.birthdate) {
      personalInfoFormData.append(
        'birthdate',
        profileData.birthdate.toISOString()
      );
    }

    personalInfoFormData.append(
      'isBirthdateVisible',
      String(profileData.isBirthdateVisible)
    );

    personalInfoFormData.append('bio', profileData.bio || '');
    personalInfoFormData.append('country', profileData.country || '');
    personalInfoFormData.append('city', profileData.city || '');

    const validEducations: IEducationData[] = educations
      .filter((education) => Object.keys(education).length > 0)
      .map((education) => ({
        id: (education as IEducationInputs)?.educationId,
        universityName: (education as IEducationInputs)?.universityName || '',
        idUniversity:
          (education as IEducationInputs)?.idUniversity === ''
            ? null
            : (education as IEducationInputs)?.idUniversity,
        specialtyName: (education as IEducationInputs)?.specialtyName || '',
        idSpecialty:
          (education as IEducationInputs)?.idSpecialty === ''
            ? null
            : (education as IEducationInputs)?.idSpecialty,
        level:
          (education as IEducationInputs)?.level ||
          EducationLevelEnum.ELEMENTARY,
        startYear: (education as IEducationInputs)?.startYear?.toDate(),
        endYear: (education as IEducationInputs)?.endYear?.toDate(),
      }));

    try {
      // Save educations first so that when updateMyProfile invalidates the
      // 'user' tag, the subsequent /me refetch sees both profile and
      // education changes.
      await createUpdateEducation({ educations: validEducations }).unwrap();
      await updateMyProfile(personalInfoFormData).unwrap();
      dispatch(gamificationApi.util?.invalidateTags(['gamification']));
      setSnackbarMessage('Profile updated successfully');
      setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbarMessage(errorHelper(error));
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      openModal();
      setOriginImageUrl(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    if (state?.moveToEducationBlock && educationSectionRef.current) {
      educationSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      if (!educationInfo?.length) {
        addEducation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]); // Intentionally omitting addEducation, educationInfo?.length - run only when state changes

  return (
    <>
      {originImageUrl && (
        <ProfilePhotoEditorModal
          isOpen={isOpen}
          onClose={() => {
            closeModal();
            setOriginImageUrl('');
          }}
          originImgSrc={originImageUrl}
          setCroppedImage={(file) => setSelectedImage(file)}
          setCroppedImagePreviewUrl={(url) => setPreviewUrl(url)}
        />
      )}
      <SectionLayout>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={mobileView ? 'flex-start' : 'center'}
            flexDirection={mobileView ? 'column-reverse' : 'row'}
            gap={mobileView ? '12px' : ''}
          >
            <Typography component="h2" variant="h2">
              Personal information
            </Typography>
            <Label
              labelType={
                profileInfo?.verified
                  ? LabelTypeEnum.VERIFIED
                  : LabelTypeEnum.UNDER_VERIFICATION
              }
              sx={{ fontSize: '0.625rem' }}
            >
              {profileInfo?.verified
                ? LabelTypeEnum.VERIFIED
                : LabelTypeEnum.UNDER_VERIFICATION}
            </Label>
          </Box>
          <Typography component="h3" variant="h3" mt="48px">
            About me
          </Typography>
          <Box mt="40px" display="flex" flexDirection="column">
            <Box
              position="relative"
              display="inline-block"
              width="fit-content"
              sx={{ '&:hover .avatar-overlay': { opacity: 1 } }}
            >
              <Avatar
                alt="Profile"
                src={previewUrl || profileInfo?.media?.avatar?.fileUrl || ''}
                sx={{ width: 140, height: 140 }}
              />
              <Box
                className="avatar-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                }}
              >
                <IconButton
                  component="label"
                  sx={{
                    width: 60,
                    height: 60,
                    color: 'white',
                    bgcolor: 'text.accent',
                  }}
                >
                  <CameraIcon />
                  <Input
                    type="file"
                    sx={{ display: 'none' }}
                    inputProps={{ accept: 'image/*' }}
                    onChange={handleImageChange}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
          <ProfilePersonalInfoForm
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          <Box ref={educationSectionRef} />
          {fields.map((field, index) => (
            <Fragment key={field.id}>
              <Divider sx={{ m: '48px 0' }} />
              <Box display="flex" justifyContent="space-between">
                <Typography component="h3" variant="h3">
                  Education {index === 0 ? '' : index + 1}
                </Typography>
                <Box
                  width="24px"
                  height="24px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => removeEducation(index)}
                >
                  <CloseX />
                </Box>
              </Box>
              <ProfileEducationForm
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                index={index}
              />
            </Fragment>
          ))}
          <CustomButton
            buttonWidth="100%"
            variantType={ButtonTypeEnum.SECONDARY}
            sx={{ borderColor: 'background.dark', mt: '36px' }}
            onClick={addEducation}
            disabled={fields.length === 5 || isSubmitting}
            type="button"
          >
            add education
          </CustomButton>

          <Divider sx={{ m: '48px 0' }} />

          <CustomButton
            buttonWidth={mobileView ? '100%' : '220px'}
            variantType={ButtonTypeEnum.PRIMARY}
            type="submit"
            disabled={(!isDirty && !selectedImage) || isSubmitting}
          >
            {isSubmitting ? 'saving...' : 'save'}
          </CustomButton>
        </Box>
      </SectionLayout>

      <SnackbarCustom
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default PersonalInfo;
